import React, { ReactNode, useEffect, useRef } from 'react';
import { useFeature, useFeatures } from '../../hooks/useFeatures';
import { FeatureCode } from '../../common/constants/featureCodes';
import { FeatureNotAvailableResponse } from '../../common/errors/featureErrors';
import UpgradePrompt from './UpgradePrompt';

interface FeatureGateProps {
	/** The feature code to check */
	featureCode: FeatureCode | string;
	/** Content to render when feature is available */
	children: ReactNode;
	/** Content to render when feature is not available - 'upgrade' shows UpgradePrompt, ReactNode for custom */
	fallback?: 'upgrade' | ReactNode;
	/** Variant for upgrade prompt when shown */
	upgradeVariant?: 'inline' | 'banner' | 'card';
	/** Whether to show the modal popup when feature is not available (default: true for 'upgrade' fallback) */
	showModal?: boolean;
}

/**
 * Conditionally renders children based on feature availability.
 * Use this to wrap entire sections or pages that require a specific feature.
 * When fallback='upgrade', automatically shows the FeatureErrorModal popup.
 *
 * @example
 * ```tsx
 * <FeatureGate featureCode={FeatureCodes.ORGANIZATION_AI_SEARCH} fallback="upgrade">
 *   <AISearchPage />
 * </FeatureGate>
 * ```
 */
/**
 * Wrapper component that conditionally renders children based on feature availability.
 * If the feature is disabled or at limit, shows an UpgradePrompt fallback
 * or hides the children entirely.
 *
 * @userstory US-FG-02, US-FG-10
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
	featureCode,
	children,
	fallback = null,
	upgradeVariant = 'card',
	showModal = true,
}) => {
	const { canUse, feature, checkResult } = useFeature(featureCode);
	const { planType } = useFeatures();
	const hasEmittedEvent = useRef(false);

	// Emit feature-not-available event when feature is not available and fallback is 'upgrade'
	useEffect(() => {
		if (!canUse && fallback === 'upgrade' && showModal && feature && !hasEmittedEvent.current) {
			hasEmittedEvent.current = true;
			const eventData: FeatureNotAvailableResponse = {
				error: 'feature_not_available',
				feature: featureCode as string,
				currentPlan: planType || 'Free',
				requiredPlan: 'Paid',
				message: `Feature ${featureCode} is not available on your current plan.`,
			};
			window.dispatchEvent(new CustomEvent('feature-not-available', { detail: eventData }));
		}
	}, [canUse, fallback, showModal, feature, featureCode, planType]);

	if (canUse) {
		return <>{children}</>;
	}

	if (fallback === 'upgrade' && feature) {
		return (
			<UpgradePrompt feature={feature} reason={checkResult.reason} variant={upgradeVariant} />
		);
	}

	return <>{fallback}</>;
};

export default FeatureGate;
